import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { X, Home, Mail, Check, Zap, Utensils, Briefcase, Globe, Heart, Users, Sparkles, Building, Dumbbell, Brain, Shield, Trophy, Apple, TrendingUp, Instagram, Flame, Leaf, Target, Crown, Star, Clock, FileText, Phone, ChefHat, Dumbbell as GymIcon, Calendar, MapPin, Award, MessageCircle } from 'lucide-react';
import { 
  HeroSection,
  TargetSection,
  MethodSection,
  VillaSection,
  TestimonialsSection,
  FAQSection,
  CTASection, 
  Footer,
  FoundersSection
} from '../components/funnel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// PDF Programs - Real links
const programPdfs = {
  thailande: "https://drive.google.com/file/d/1example-thailande/view",
  bali: "https://drive.google.com/file/d/1example-bali/view",
  marrakech: "https://drive.google.com/file/d/1example-marrakech/view"
};

// Countdown Hook
const useCountdown = (targetDate) => {
  const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  
  useEffect(() => {
    const calculateTimeLeft = () => {
      const now = new Date().getTime();
      const target = new Date(targetDate).getTime();
      const difference = target - now;
      
      if (difference > 0) {
        setTimeLeft({
          days: Math.floor(difference / (1000 * 60 * 60 * 24)),
          hours: Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((difference % (1000 * 60)) / 1000)
        });
      }
    };
    
    calculateTimeLeft();
    const timer = setInterval(calculateTimeLeft, 1000);
    return () => clearInterval(timer);
  }, [targetDate]);
  
  return timeLeft;
};

// Experience categories organized by section - Premium design for entrepreneurs
const experienceCategories = {
  homme: [
    {
      category: 'CORPS & CONSCIENCE',
      subtitle: 'Matin - Reconnecte-toi',
      icon: Dumbbell,
      color: '#D4A574',
      options: [
        { id: 'combat', title: 'SPORT DE COMBAT', description: 'Boxe • Discipline • Dépassement' },
        { id: 'velo', title: 'VÉLO ÉLECTRIQUE', description: 'Plage • Campagne • Découverte' },
        { id: 'surf', title: 'SURF / KITESURF', description: 'Initiation • Océan • Adrénaline' },
        { id: 'meditation', title: 'MÉDITATION & RESPIRATION', description: 'Mindfulness • Écriture • Journaling' },
        { id: 'cercle', title: 'CERCLES DE PAROLE', description: 'Partage • Authenticité • Connexion' },
      ]
    },
    {
      category: 'BUSINESS',
      subtitle: 'Après-midi - Développe ton empire',
      icon: Briefcase,
      color: '#1a1a1a',
      options: [
        { id: 'strategie', title: 'STRATÉGIE', description: 'Diagnostic • Plan 90 jours • Priorités' },
        { id: 'ia', title: 'IA & AUTOMATISATION', description: 'Outils concrets • Productivité' },
        { id: 'branding', title: 'BRANDING', description: 'Positionnement • Image • Personal brand' },
        { id: 'hotseat', title: 'HOT SEAT', description: 'Résolution de blocages • Peer coaching' },
      ]
    },
    {
      category: 'EXPÉRIENCES SIGNATURE',
      subtitle: 'Moments inoubliables',
      icon: Star,
      color: '#D4A574',
      options: [
        { id: 'cayenne', title: 'CAYENNE EXPERIENCE', description: 'Pilotage sportif • Adrénaline' },
        { id: 'hammam', title: 'HAMMAM & MASSAGE', description: 'Traditionnel • Récupération' },
        { id: 'table', title: 'TABLE DES DIRIGEANTS', description: 'Dîner avec invité entrepreneur' },
      ]
    }
  ],
  femme: [
    {
      category: 'CORPS & CONSCIENCE',
      subtitle: 'Matin - Reconnecte-toi',
      icon: Heart,
      color: '#B76E79',
      options: [
        { id: 'combat', title: 'SPORT DE COMBAT', description: 'Boxe • Discipline • Confiance' },
        { id: 'velo', title: 'VÉLO ÉLECTRIQUE', description: 'Plage • Campagne • Découverte' },
        { id: 'surf', title: 'SURF / KITESURF', description: 'Initiation • Océan • Liberté' },
        { id: 'meditation', title: 'MÉDITATION & RESPIRATION', description: 'Mindfulness • Écriture • Journaling' },
        { id: 'cercle', title: 'CERCLES DE PAROLE', description: 'Sororité • Authenticité • Partage' },
      ]
    },
    {
      category: 'BUSINESS',
      subtitle: 'Après-midi - Développe ton influence',
      icon: Crown,
      color: '#B76E79',
      options: [
        { id: 'strategie', title: 'STRATÉGIE', description: 'Diagnostic • Plan 90 jours • Priorités' },
        { id: 'ia', title: 'IA & AUTOMATISATION', description: 'Outils concrets • Productivité' },
        { id: 'branding', title: 'BRANDING', description: 'Positionnement • Image • Personal brand' },
        { id: 'hotseat', title: 'HOT SEAT', description: 'Résolution de blocages • Peer coaching' },
      ]
    },
    {
      category: 'EXPÉRIENCES SIGNATURE',
      subtitle: 'Moments inoubliables',
      icon: Sparkles,
      color: '#9C7A97',
      options: [
        { id: 'cayenne', title: 'CAYENNE EXPERIENCE', description: 'Pilotage sportif • Adrénaline' },
        { id: 'hammam', title: 'HAMMAM & MASSAGE', description: 'Traditionnel • Soins beauté' },
        { id: 'table', title: 'TABLE DES DIRIGEANTES', description: 'Dîner avec invitée entrepreneure' },
      ]
    }
  ]
};

// Tags for Step 1
const tagsHomme = ['Sport', 'Alimentation', 'Business', 'Bien-être', 'IA', 'Villa premium'];
const tagsFemme = ['Sport', 'Alimentation', 'Business', 'Bien-être', 'Spa', 'Villa premium'];

// Destination unique - Essaouira
const destinationEssaouira = {
  id: 1,
  destination: "ESSAOUIRA",
  location: "Villa Juba, Maroc",
  image: "https://customer-assets-m6fa6gv7.emergentagent.net/job_c9f8c8c9-6f95-436f-97e4-46853bac1308/artifacts/xs6eu0ve_ef.jpeg",
  date: "Dates à annoncer",
  departureDate: null,
  placesRestantes: 10,
  placesTotal: 12,
  statut: "places disponibles",
  statusIcon: "check",
  disponible: true,
  duration: "6 nuits",
  price: "6 500",
  testimonial: {
    text: "L'énergie d'Essaouira et le coaching m'ont permis de prendre du recul sur mon business",
    author: "Marc",
    year: "2024",
    rating: 4.9
  },
  programPdf: "https://customer-assets-m6fa6gv7.emergentagent.net/job_c9f8c8c9-6f95-436f-97e4-46853bac1308/artifacts/njspva0q_VIVANT-presentation.pdf"
};

// Keep for backward compatibility
const destinationsHomme = [destinationEssaouira];
const destinationsFemme = [destinationEssaouira];

const FunnelPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Gender, 2: Experience, 3: Destination, 4: Final
  const [selectedGender, setSelectedGender] = useState(null);
  const [selectedExperiences, setSelectedExperiences] = useState([]);
  const [selectedDestination, setSelectedDestination] = useState(null);
  const [showEmailPopup, setShowEmailPopup] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Scroll to top when step changes
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [step]);

  // Colors based on gender
  const isFemme = selectedGender === 'femme';
  const accentColor = isFemme ? '#B76E79' : '#D4A574';
  const accentBg = isFemme ? 'bg-[#B76E79]' : 'bg-[#D4A574]';
  const accentText = isFemme ? 'text-[#B76E79]' : 'text-[#D4A574]';
  const accentHover = isFemme ? 'hover:bg-[#A25D68]' : 'hover:bg-[#C39563]';
  const accentBorder = isFemme ? 'border-[#B76E79]' : 'border-[#D4A574]';

  const handleGenderSelect = (gender) => {
    setSelectedGender(gender);
    // Skip step 2 (experiences) and go directly to step 3 (destinations)
    setStep(3);
  };

  const handleExperienceToggle = (id) => {
    setSelectedExperiences(prev => 
      prev.includes(id) 
        ? prev.filter(e => e !== id)
        : [...prev, id]
    );
  };

  const handleDestinationSelect = (dest) => {
    setSelectedDestination(dest);
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    if (email && !isSubmitting) {
      setIsSubmitting(true);
      try {
        const response = await fetch(`${API_URL}/api/subscribe`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email,
            gender: selectedGender,
            experiences: selectedExperiences,
            destination: selectedDestination?.destination || null,
            source: `funnel_step${step}`
          })
        });
        
        if (response.ok) {
          setEmailSubmitted(true);
          setTimeout(() => {
            setShowEmailPopup(false);
            setEmailSubmitted(false);
            setEmail('');
          }, 2000);
        }
      } catch (error) {
        console.error('Error submitting email:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const goToNextStep = () => {
    if (step === 2 && selectedExperiences.length > 0) {
      setStep(3);
    } else if (step === 3 && selectedDestination) {
      setStep(4);
    }
  };

  const goBack = () => {
    if (step > 1) {
      setStep(step - 1);
    }
  };

  const goHome = () => {
    setStep(1);
    setSelectedGender(null);
    setSelectedExperiences([]);
    setSelectedDestination(null);
  };

  // Email Popup Component
  const EmailPopup = () => (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl p-8 max-w-md w-full relative">
        <button 
          onClick={() => setShowEmailPopup(false)}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
        >
          <X size={24} />
        </button>
        
        {emailSubmitted ? (
          <div className="text-center py-8">
            <div className={`w-16 h-16 ${accentBg} rounded-full flex items-center justify-center mx-auto mb-4`}>
              <Check className="text-white" size={32} />
            </div>
            <h3 className="font-italiana text-2xl tracking-wider mb-2">MERCI !</h3>
            <p className="text-gray-600 text-sm">Tu recevras le programme par email.</p>
          </div>
        ) : (
          <>
            <div className="text-center mb-6">
              <Mail className={`w-12 h-12 ${accentText} mx-auto mb-4`} />
              <h3 className="font-italiana text-2xl tracking-wider mb-2">REÇOIS LE PROGRAMME</h3>
              <p className="text-gray-600 text-sm">
                Entre ton email pour recevoir tous les détails de l'expérience VIVANT{isFemme ? 'E' : ''}.
              </p>
            </div>
            <form onSubmit={handleEmailSubmit}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="ton@email.com"
                className={`w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-[${accentColor}] mb-4`}
                required
              />
              <button
                type="submit"
                disabled={isSubmitting}
                className={`w-full ${accentBg} text-white py-3 rounded-xl text-sm tracking-wider font-medium ${accentHover} transition-colors disabled:opacity-50`}
              >
                {isSubmitting ? 'ENVOI...' : 'RECEVOIR LE PROGRAMME'}
              </button>
            </form>
          </>
        )}
      </div>
    </div>
  );

  // Navigation Header
  const Header = () => (
    <nav className="fixed top-0 left-0 right-0 z-40 bg-white/98 backdrop-blur-xl border-b border-gray-100">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 py-3 md:py-4 flex justify-between items-center">
        <button onClick={goHome} className="font-italiana text-xl md:text-2xl tracking-[0.4em] text-gray-900">
          <span className={accentText}>V</span>IVANT{isFemme ? 'E' : ''}
        </button>
        
        <div className="flex items-center gap-4">
          <button
            onClick={() => setShowEmailPopup(true)}
            className={`hidden md:flex items-center gap-2 text-xs tracking-[0.1em] ${accentText} hover:opacity-80 transition-opacity`}
          >
            <Mail size={16} />
            RECEVOIR LE PROGRAMME
          </button>
          <Link
            to="/accueil"
            state={{ gender: selectedGender }}
            className="flex items-center gap-2 text-gray-500 hover:text-gray-900 text-xs tracking-[0.15em] transition-colors"
          >
            <Home size={16} />
            <span className="hidden md:inline">ACCUEIL</span>
          </Link>
        </div>
      </div>
    </nav>
  );

  // Progress Bar
  const ProgressBar = () => (
    <div className="fixed top-16 left-0 right-0 z-30 bg-white border-b border-gray-100 py-2">
      <div className="max-w-[600px] mx-auto px-4">
        <div className="flex items-center justify-between mb-2">
          <span className="text-xs text-gray-400">Étape {step}/4</span>
          <button 
            onClick={() => setShowEmailPopup(true)}
            className={`md:hidden text-xs ${accentText}`}
          >
            <Mail size={16} />
          </button>
        </div>
        <div className="h-1 bg-gray-100 rounded-full overflow-hidden">
          <div 
            className={`h-full ${accentBg} transition-all duration-500`}
            style={{ width: `${(step / 4) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );

  // STEP 1: Landing Page - Using refactored components
  if (step === 1) {
    const handleJoinExperience = () => handleGenderSelect('homme');
    
    return (
      <div className="min-h-screen bg-black flex flex-col">
        <HeroSection onJoinExperience={handleJoinExperience} />
        <TargetSection onJoinExperience={handleJoinExperience} />
        <MethodSection />
        <VillaSection />
        <FoundersSection />
        <TestimonialsSection />
        <FAQSection />
        <CTASection onJoinExperience={handleJoinExperience} />
        <Footer />
      </div>
    );
  }

  // STEP 2: Experience Selection - Attractive & Premium
  if (step === 2) {
    const categories = experienceCategories[selectedGender] || experienceCategories.homme;
    
    return (
      <div className="min-h-screen bg-[#FAFAFA]">
        <Header />
        <ProgressBar />
        {showEmailPopup && <EmailPopup />}

        <div className="pt-28 pb-28 px-4 md:px-8">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="text-center mb-8">
              <p className={`${accentText} text-xs tracking-[0.3em] mb-3`}>ÉTAPE 2/4</p>
              <h1 className="font-italiana text-3xl md:text-4xl tracking-[0.12em] text-gray-900 mb-2">
                COMPOSE TON <span className={accentText}>EXPÉRIENCE</span>
              </h1>
              <p className="text-gray-400 text-sm">
                Qu'est-ce qui t'attire ? Sélectionne autant d'options que tu veux.
              </p>
            </div>

            {/* Categories */}
            <div className="space-y-6">
              {categories.map((cat) => (
                <div key={cat.category}>
                  {/* Category Title */}
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`w-1 h-6 rounded-full`} style={{ backgroundColor: cat.color }} />
                    <h2 className="text-xs tracking-[0.2em] text-gray-500 font-medium">{cat.category}</h2>
                  </div>
                  
                  {/* Options */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                    {cat.options.map((option) => {
                      const isSelected = selectedExperiences.includes(option.id);
                      return (
                        <button
                          key={option.id}
                          onClick={() => handleExperienceToggle(option.id)}
                          className={`group relative p-4 rounded-xl text-left transition-all duration-200 border ${
                            isSelected 
                              ? 'bg-[#1a1a1a] border-[#1a1a1a] text-white shadow-lg scale-[1.02]' 
                              : 'bg-white border-gray-100 hover:border-gray-200 hover:shadow-md'
                          }`}
                        >
                          <div className="flex items-start justify-between gap-2">
                            <div className="flex-1">
                              <h3 className={`text-sm font-medium leading-tight mb-1 ${isSelected ? 'text-white' : 'text-gray-900'}`}>
                                {option.title}
                              </h3>
                              <p className={`text-[11px] leading-relaxed ${isSelected ? 'text-white/60' : 'text-gray-400'}`}>
                                {option.description}
                              </p>
                            </div>
                            <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                              isSelected 
                                ? accentBg 
                                : 'border-2 border-gray-200 group-hover:border-gray-300'
                            }`}>
                              {isSelected && <Check size={12} className="text-white" />}
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Fixed Bottom Navigation */}
        <div className="fixed bottom-0 left-0 right-0 bg-white/95 backdrop-blur-sm border-t border-gray-100 py-4 px-4 z-20">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <button
              onClick={goBack}
              className="text-gray-400 hover:text-gray-600 text-xs tracking-wider transition-colors"
            >
              ← RETOUR
            </button>
            <div className="flex items-center gap-4">
              {selectedExperiences.length > 0 && (
                <span className="text-xs text-gray-400">
                  {selectedExperiences.length} sélectionné{selectedExperiences.length > 1 ? 's' : ''}
                </span>
              )}
              <button
                onClick={goToNextStep}
                disabled={selectedExperiences.length === 0}
                className={`${accentBg} text-white px-8 py-3 rounded-xl text-sm tracking-wider font-medium ${accentHover} transition-all disabled:opacity-30 disabled:cursor-not-allowed shadow-lg`}
              >
                CONTINUER
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // STEP 3: Destination - Essaouira Visual Presentation
  if (step === 3) {
    const essaouira = destinationsHomme[0]; // Only Essaouira for now
    
    return (
      <div className="min-h-screen bg-black">
        <Header />
        {showEmailPopup && <EmailPopup />}

        {/* Hero Section */}
        <div className="relative h-[60vh] min-h-[400px]">
          <img
            src="https://customer-assets-m6fa6gv7.emergentagent.net/job_c9f8c8c9-6f95-436f-97e4-46853bac1308/artifacts/xs6eu0ve_ef.jpeg"
            alt="Villa Juba Essaouira"
            className="absolute inset-0 w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black" />
          
          <div className="absolute inset-0 flex flex-col justify-end p-6 md:p-12">
            <p className="text-[#D4A574] text-xs tracking-[0.3em] mb-3">DESTINATION</p>
            <h1 className="font-italiana text-4xl md:text-6xl tracking-[0.15em] text-white mb-2">
              ESSAOUIRA
            </h1>
            <p className="text-white/70 text-lg mb-4">Villa Juba, Maroc</p>
            <div className="flex flex-wrap gap-4 text-sm text-white/60">
              <span>6 nuits</span>
              <span>•</span>
              <span>10-12 entrepreneurs</span>
              <span>•</span>
              <span className="text-[#D4A574]">6 500€</span>
            </div>
          </div>
        </div>

        {/* Activities - Icon Cards with Benefits */}
        <div className="bg-black py-16 px-6">
          <div className="max-w-6xl mx-auto">
            <p className="text-[#D4A574] text-xs tracking-[0.3em] mb-4 text-center">TON PROGRAMME</p>
            <h2 className="font-italiana text-3xl md:text-4xl text-white mb-4 text-center tracking-[0.1em]">
              CE QUI T'ATTEND À <span className="text-[#D4A574]">ESSAOUIRA</span>
            </h2>
            <p className="text-white/50 text-center text-sm mb-12 max-w-2xl mx-auto">
              6 jours pour te couper du monde, te recentrer et revenir transformé.
            </p>

            {/* Icon Cards Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-6 mb-12">
              {/* Mindset & Discipline */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-[#D4A574]/50 transition-all group">
                <div className="w-12 h-12 bg-[#D4A574]/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#D4A574]/30 transition-colors">
                  <svg className="w-6 h-6 text-[#D4A574]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                  </svg>
                </div>
                <p className="text-[#D4A574] text-[10px] tracking-wider mb-1">MENTAL</p>
                <h3 className="text-white font-italiana text-lg tracking-wider mb-2">Discipline & Force</h3>
                <p className="text-white/50 text-xs leading-relaxed">Développe ta résilience mentale. Repousse tes limites. Redeviens inébranlable.</p>
              </div>

              {/* Business & Stratégie */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-[#D4A574]/50 transition-all group">
                <div className="w-12 h-12 bg-[#D4A574]/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#D4A574]/30 transition-colors">
                  <svg className="w-6 h-6 text-[#D4A574]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                </div>
                <p className="text-[#D4A574] text-[10px] tracking-wider mb-1">BUSINESS</p>
                <h3 className="text-white font-italiana text-lg tracking-wider mb-2">Stratégie & Scale</h3>
                <p className="text-white/50 text-xs leading-relaxed">Workshops IA, stratégie de croissance. Repars avec un plan d'action béton pour scaler.</p>
              </div>

              {/* Déconnexion */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-[#D4A574]/50 transition-all group">
                <div className="w-12 h-12 bg-[#D4A574]/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#D4A574]/30 transition-colors">
                  <svg className="w-6 h-6 text-[#D4A574]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 5.636a9 9 0 010 12.728m0 0l-2.829-2.829m2.829 2.829L21 21M15.536 8.464a5 5 0 010 7.072m0 0l-2.829-2.829m-4.243 2.829a4.978 4.978 0 01-1.414-2.83m-1.414 5.658a9 9 0 01-2.167-9.238m7.824 2.167a1 1 0 111.414 1.414m-1.414-1.414L3 3" />
                  </svg>
                </div>
                <p className="text-[#D4A574] text-[10px] tracking-wider mb-1">RESET</p>
                <h3 className="text-white font-italiana text-lg tracking-wider mb-2">Se Couper du Monde</h3>
                <p className="text-white/50 text-xs leading-relaxed">Zéro distraction. Zéro notification. 6 jours pour te reconnecter à l'essentiel.</p>
              </div>

              {/* Corps & Performance */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-[#D4A574]/50 transition-all group">
                <div className="w-12 h-12 bg-[#D4A574]/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#D4A574]/30 transition-colors">
                  <svg className="w-6 h-6 text-[#D4A574]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                </div>
                <p className="text-[#D4A574] text-[10px] tracking-wider mb-1">PHYSIQUE</p>
                <h3 className="text-white font-italiana text-lg tracking-wider mb-2">Corps & Énergie</h3>
                <p className="text-white/50 text-xs leading-relaxed">Boxe, kitesurf, vélo dunes. Retrouve l'énergie de tes 25 ans. Deviens plus fort physiquement.</p>
              </div>

              {/* Réseau VIVANT */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-[#D4A574]/50 transition-all group">
                <div className="w-12 h-12 bg-[#D4A574]/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#D4A574]/30 transition-colors">
                  <svg className="w-6 h-6 text-[#D4A574]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <p className="text-[#D4A574] text-[10px] tracking-wider mb-1">RÉSEAU</p>
                <h3 className="text-white font-italiana text-lg tracking-wider mb-2">Réseau VIVANT</h3>
                <p className="text-white/50 text-xs leading-relaxed">Rejoins un réseau planétaire. Fondateurs, membres, dirigeants. Des connexions pour la vie.</p>
              </div>

              {/* Récupération */}
              <div className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-[#D4A574]/50 transition-all group">
                <div className="w-12 h-12 bg-[#D4A574]/20 rounded-xl flex items-center justify-center mb-4 group-hover:bg-[#D4A574]/30 transition-colors">
                  <svg className="w-6 h-6 text-[#D4A574]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                </div>
                <p className="text-[#D4A574] text-[10px] tracking-wider mb-1">RÉCUPÉRATION</p>
                <h3 className="text-white font-italiana text-lg tracking-wider mb-2">Recharge Totale</h3>
                <p className="text-white/50 text-xs leading-relaxed">Villa privée, piscine, hammam, massage. Récupère comme un athlète pro. Reviens à 100%.</p>
              </div>
            </div>

            {/* Inclusions */}
            <div className="bg-white/5 rounded-2xl p-6 md:p-8 mb-8">
              <h3 className="text-white font-italiana text-xl tracking-[0.1em] mb-6 text-center">TOUT EST INCLUS</h3>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
                <div className="text-white/70 text-sm">
                  <div className="w-10 h-10 mx-auto mb-2 bg-[#D4A574]/20 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#D4A574]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" /></svg>
                  </div>
                  Chambre individuelle
                </div>
                <div className="text-white/70 text-sm">
                  <div className="w-10 h-10 mx-auto mb-2 bg-[#D4A574]/20 rounded-full flex items-center justify-center">
                    <ChefHat className="w-5 h-5 text-[#D4A574]" />
                  </div>
                  Tous les repas inclus
                </div>
                <div className="text-white/70 text-sm">
                  <div className="w-10 h-10 mx-auto mb-2 bg-[#D4A574]/20 rounded-full flex items-center justify-center">
                    <Dumbbell className="w-5 h-5 text-[#D4A574]" />
                  </div>
                  Coachings & formations
                </div>
                <div className="text-white/70 text-sm">
                  <div className="w-10 h-10 mx-auto mb-2 bg-[#D4A574]/20 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#D4A574]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>
                  </div>
                  Transferts aéroport
                </div>
                <div className="text-white/70 text-sm">
                  <div className="w-10 h-10 mx-auto mb-2 bg-[#D4A574]/20 rounded-full flex items-center justify-center">
                    <Sparkles className="w-5 h-5 text-[#D4A574]" />
                  </div>
                  Hammam & massage
                </div>
                <div className="text-white/70 text-sm">
                  <div className="w-10 h-10 mx-auto mb-2 bg-[#D4A574]/20 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#D4A574]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" /></svg>
                  </div>
                  Shooting pro
                </div>
                <div className="text-white/70 text-sm">
                  <div className="w-10 h-10 mx-auto mb-2 bg-[#D4A574]/20 rounded-full flex items-center justify-center">
                    <svg className="w-5 h-5 text-[#D4A574]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" /></svg>
                  </div>
                  Kit de bienvenue
                </div>
                <div className="text-white/70 text-sm">
                  <div className="w-10 h-10 mx-auto mb-2 bg-[#D4A574]/20 rounded-full flex items-center justify-center">
                    <Phone className="w-5 h-5 text-[#D4A574]" />
                  </div>
                  Suivi post-camp
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="text-center">
              <button
                onClick={() => {
                  setSelectedDestination(essaouira);
                  setStep(4);
                }}
                className="bg-[#D4A574] hover:bg-[#C39563] text-white px-12 py-5 rounded-full text-sm tracking-[0.2em] font-medium transition-all hover:scale-105 mb-4"
              >
                JE RÉSERVE MA PLACE
              </button>
              <p className="text-white/40 text-xs">Places limitées • Sélection sur dossier</p>
            </div>
          </div>
        </div>

        {/* Back button */}
        <div className="fixed bottom-6 left-6 z-20">
          <button
            onClick={goBack}
            className="text-white/50 hover:text-white text-xs tracking-wider transition-colors flex items-center gap-2"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            RETOUR
          </button>
        </div>
      </div>
    );
  }

  // STEP 4: Final Options - Minimalist Premium Design
  if (step === 4) {
    const priceRange = '6 500';
    
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <ProgressBar />
        {showEmailPopup && <EmailPopup />}

        <div className="pt-32 pb-16 px-4 md:px-8">
          <div className="max-w-3xl mx-auto">
            {/* Header */}
            <div className="text-center mb-12">
              <p className={`${accentText} text-xs tracking-[0.3em] mb-4`}>ÉTAPE 4/4</p>
              <h1 className="font-italiana text-3xl md:text-4xl lg:text-5xl tracking-[0.15em] text-gray-900 mb-4">
                PRÊT{isFemme ? 'E' : ''} À <span className={accentText}>PARTIR</span> ?
              </h1>
            </div>

            {/* Destination Summary Card - Premium Dark Style */}
            {selectedDestination && (
              <div className="bg-[#1a1a1a] rounded-2xl p-6 md:p-8 mb-8 text-white">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
                  <div>
                    <p className="text-xs text-white/50 tracking-[0.2em] mb-1">DESTINATION</p>
                    <h2 className="font-italiana text-2xl md:text-3xl tracking-[0.15em]">{selectedDestination.destination}</h2>
                    <p className="text-white/60 text-sm mt-1">{selectedDestination.location}</p>
                  </div>
                  <div className="text-left md:text-right">
                    <p className={`font-italiana text-3xl md:text-4xl ${accentText}`}>{selectedDestination.price || priceRange}€</p>
                    <p className="text-white/50 text-xs tracking-wider">HORS VOLS</p>
                  </div>
                </div>
                
                <div className="border-t border-white/10 pt-6">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
                    <div>
                      <p className="text-white/40 text-[10px] tracking-wider mb-1">DATES</p>
                      <p className="text-white text-sm">{selectedDestination.date}</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-[10px] tracking-wider mb-1">DURÉE</p>
                      <p className="text-white text-sm">{selectedDestination.duration || '6 nuits'}</p>
                    </div>
                    <div>
                      <p className="text-white/40 text-[10px] tracking-wider mb-1">PLACES</p>
                      <p className={`text-sm ${selectedDestination.placesRestantes <= 3 ? 'text-orange-400' : 'text-white'}`}>
                        {selectedDestination.disponible ? `${selectedDestination.placesRestantes} restantes` : 'À venir'}
                      </p>
                    </div>
                    <div>
                      <p className="text-white/40 text-[10px] tracking-wider mb-1">GROUPE</p>
                      <p className="text-white text-sm">10-12 max</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Programme Journalier */}
            <div className="mb-8">
              <p className="text-xs text-gray-400 tracking-[0.2em] mb-4">PROGRAMME JOURNALIER</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-2 h-2 rounded-full ${accentBg}`} />
                    <span className="text-xs tracking-[0.15em] text-gray-500">MATIN</span>
                  </div>
                  <h3 className="font-medium text-gray-900 mb-2">Corps & Conscience</h3>
                  <p className="text-sm text-gray-600">Sport de combat • Vélo électrique plage • Surf/Kitesurf • Méditation • Cercles de parole</p>
                </div>
                <div className="bg-gray-50 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-3">
                    <div className={`w-2 h-2 rounded-full ${accentBg}`} />
                    <span className="text-xs tracking-[0.15em] text-gray-500">APRÈS-MIDI</span>
                  </div>
                  <h3 className="font-medium text-gray-900 mb-2">Business</h3>
                  <p className="text-sm text-gray-600">Stratégie & plan 90 jours • IA & automatisation • Branding • Hot Seat</p>
                </div>
              </div>
            </div>

            {/* Inclus - Clean List */}
            <div className="mb-8">
              <p className="text-xs text-gray-400 tracking-[0.2em] mb-4">CE QUI EST INCLUS</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
                {[
                  'Chambre individuelle + SDB privée',
                  'Pension complète (chef privé)',
                  'Tous les ateliers & coachings',
                  'Activités sport & plein air',
                  'Hammam & massage traditionnel',
                  'Shooting portrait professionnel',
                  'Transferts aéroport',
                  'Kit de bienvenue VIVANT',
                  'Suivi post-camp (J+30 & J+90)',
                  'Accès réseau alumni'
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-100">
                    <div className={`w-1.5 h-1.5 rounded-full ${accentBg}`} />
                    <span className="text-sm text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Expériences Signature */}
            <div className="mb-8">
              <p className="text-xs text-gray-400 tracking-[0.2em] mb-4">EXPÉRIENCES SIGNATURE</p>
              <div className="flex flex-wrap gap-2">
                {[
                  'Cayenne Experience',
                  'Soirée Gnaoua',
                  'Hammam traditionnel',
                  'Surf / Kitesurf',
                  'Table des dirigeants',
                  'Visite médina'
                ].map((exp, i) => (
                  <span key={i} className={`text-xs px-3 py-1.5 rounded-full border ${accentBorder} ${isFemme ? 'bg-[#B76E79]/10' : 'bg-[#D4A574]/10'} text-gray-700`}>
                    {exp}
                  </span>
                ))}
              </div>
            </div>

            {/* Avantages - Subtle */}
            <div className="flex flex-wrap justify-center gap-6 mb-10 text-xs text-gray-500">
              <span>Facturable en formation</span>
              <span className="text-gray-300">•</span>
              <span>Déductible des charges</span>
            </div>

            {/* CTA Buttons - Clean */}
            <div className="space-y-3">
              <Link
                to="/candidature"
                state={{ 
                  gender: selectedGender, 
                  experiences: selectedExperiences,
                  destination: selectedDestination 
                }}
                className={`block w-full ${accentBg} ${accentHover} text-white py-4 rounded-xl text-center text-sm tracking-[0.15em] font-medium transition-all hover:shadow-lg`}
              >
                CANDIDATER
              </Link>
              
              <div className="grid grid-cols-2 gap-3">
                {selectedDestination?.programPdf && (
                  <a
                    href={selectedDestination.programPdf}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block w-full border border-gray-200 hover:border-gray-300 text-gray-700 py-3 rounded-xl text-center text-xs tracking-[0.1em] transition-all"
                  >
                    VOIR LE PROGRAMME
                  </a>
                )}
                <Link
                  to="/accueil"
                  state={{ gender: selectedGender }}
                  className={`block w-full border border-gray-200 hover:border-gray-300 text-gray-700 py-3 rounded-xl text-center text-xs tracking-[0.1em] transition-all ${!selectedDestination?.programPdf ? 'col-span-2' : ''}`}
                >
                  EN SAVOIR PLUS
                </Link>
              </div>
            </div>

            {/* Trust - Minimal */}
            <div className="flex justify-center gap-8 mt-8 text-xs text-gray-400">
              <span className="flex items-center gap-2">
                <Shield size={14} />
                Satisfait{isFemme ? 'e' : ''} ou remboursé{isFemme ? 'e' : ''} 48h
              </span>
              <span className="flex items-center gap-2">
                <Phone size={14} />
                Appel découverte offert
              </span>
            </div>

            {/* Back button */}
            <div className="text-center mt-10">
              <button
                onClick={goBack}
                className="text-gray-400 hover:text-gray-600 text-xs tracking-wider transition-colors"
              >
                ← RETOUR
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
};

export default FunnelPage;
